<!--
  @component FleetStatusWallBlock — every app's live state, at a glance.
  tier:   deployments block

  A tile per project: status LED, letter-mark, live URL, last deploy. The card
  body navigates to that project's Releases tab; the URL opens the live site
  (siblings — no nested interactives). Real status via the per-project rollup
  over the live deploy-event stream.
-->
<script lang="ts">
  import { push } from "svelte-spa-router";
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useProvenanceIO } from "../../../../global/io/nodes/deployment/ProvenanceIO.svelte";
  import {
    projectRollup,
    relTime,
    initial,
    projectGradient,
    type ProjectStatus,
  } from "../../home/home-util.js";
  import { liveUrl, scopeProjects, scopeEvents } from "./fleet-util.js";
  import { useScopeIO } from "./ScopeIO.svelte";

  const identity = useIdentityIO();
  const provenance = useProvenanceIO();
  const scope = useScopeIO();

  const scopedProjects = $derived(scopeProjects(identity.projects, scope.projectIds));
  const scopedEvents = $derived(scopeEvents(provenance.events, scope.projectIds));

  interface Row {
    readonly id: string;
    readonly name: string;
    readonly status: ProjectStatus;
    readonly url: string;
    readonly lastMs: number;
  }

  const rows = $derived.by<Row[]>(() => {
    const roll = projectRollup(scopedEvents);
    return scopedProjects
      .map((p) => {
        const r = roll.get(p.id);
        return {
          id: p.id,
          name: p.name,
          status: r?.status ?? "idle",
          url: liveUrl(p),
          lastMs: r?.lastActivityMs ?? p.createdAtMs,
        } satisfies Row;
      })
      .sort((a, b) => b.lastMs - a.lastMs);
  });

  const STATUS_LABEL: Record<ProjectStatus, string> = {
    live: "Live",
    building: "Deploying",
    error: "Issue",
    idle: "Idle",
  };

  function host(url: string): string {
    try { return new URL(url).host; } catch { return url.replace(/^https?:\/\//, ""); }
  }
</script>

<section class="wall">
  <header class="head">
    <h2>Fleet status</h2>
    <span class="count">{rows.length}</span>
  </header>

  {#if rows.length === 0}
    <p class="empty">No projects yet.</p>
  {:else}
    <div class="grid">
      {#each rows as r (r.id)}
        <div class="tile">
          <button type="button" class="tile-main" onclick={() => push(`/projects/${r.id}/releases`)}>
            <span class="tile-top">
              <span class="mark" style="background: {projectGradient(r.id)}">{initial(r.name)}</span>
              <span class="name" title={r.name}>{r.name}</span>
              <span class="led {r.status}" title={STATUS_LABEL[r.status]}></span>
            </span>
            <span class="foot">
              <span class="status-txt {r.status}">{STATUS_LABEL[r.status]}</span>
              <span class="dot-sep">·</span>
              <span class="last">{relTime(r.lastMs)}</span>
            </span>
          </button>
          <a
            class="url"
            href={r.url}
            target="_blank"
            rel="noopener noreferrer"
            title={r.url}
          >{host(r.url)}</a>
        </div>
      {/each}
    </div>
  {/if}
</section>

<style>
  .wall {
    display: flex; flex-direction: column; gap: var(--space-3);
    padding: var(--space-5);
    background: var(--glass-fill);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-xl, 18px);
    backdrop-filter: var(--glass-blur); -webkit-backdrop-filter: var(--glass-blur);
    font-family: var(--type-font);
  }
  .head { display: flex; align-items: center; gap: var(--space-2); }
  h2 { margin: 0; font-size: 15px; font-weight: 600; letter-spacing: -0.01em; color: var(--text-primary); }
  .count {
    font-size: 11px; font-weight: 600; color: var(--text-tertiary);
    background: var(--bg-hover); border-radius: var(--radius-pill); padding: 1px 8px;
  }
  .empty { margin: 0; font-size: 13px; color: var(--text-tertiary); }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
    gap: var(--space-3);
  }
  .tile {
    display: flex; flex-direction: column; gap: var(--space-2);
    padding: var(--space-3) var(--space-4);
    background: var(--surface-sunken);
    border: 0.5px solid var(--border-subtle);
    border-radius: var(--radius-lg, 14px);
    transition: border-color var(--ease-fast), background var(--ease-fast);
  }
  .tile:hover { border-color: var(--border-focus); background: var(--bg-hover); }
  .tile-main {
    display: flex; flex-direction: column; gap: var(--space-2);
    background: transparent; border: 0; padding: 0; margin: 0;
    text-align: left; font: inherit; color: inherit; cursor: pointer; width: 100%;
  }
  .tile-top { display: flex; align-items: center; gap: var(--space-2); }
  .mark {
    width: 24px; height: 24px; border-radius: var(--radius-sm); flex-shrink: 0;
    display: grid; place-items: center;
    font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.92);
  }
  .name {
    flex: 1; min-width: 0; font-size: 13.5px; font-weight: 500; color: var(--text-primary);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .led { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; background: var(--text-tertiary); }
  .led.live { background: var(--success); box-shadow: 0 0 0 3px color-mix(in oklab, var(--success) 22%, transparent); }
  .led.building { background: var(--warning); }
  .led.error { background: var(--error); }
  .foot { display: flex; align-items: center; gap: 6px; font-size: 11.5px; color: var(--text-tertiary); }
  .status-txt.live { color: var(--success); }
  .status-txt.building { color: var(--warning); }
  .status-txt.error { color: var(--error); }
  .dot-sep { opacity: 0.5; }
  .url {
    font-family: var(--type-mono); font-size: 11.5px; color: var(--text-secondary);
    text-decoration: none; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .url:hover { color: var(--accent-text, var(--text-primary)); text-decoration: underline; }
</style>
